import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDirection } from '../../context/DirectionContext';
import { translate as tr } from '../../i18n/translations';
import apiService from '../../services/api';
import { SharedParameter } from '../../types/api';

export const SCALES = [5, 6, 7, 8, 9, 10];

/** Server error text; NestJS validation sends `message` as an array. */
export const apiErrorMessage = (e: any, fallback: string): string => {
  const m = e?.response?.data?.message;
  return Array.isArray(m) ? m.join(', ') : m || fallback;
};

interface Props {
  open: boolean;
  initialName?: string;
  onClose: () => void;
  onCreated: (p: SharedParameter) => void;
}

const NewParameterDialog: React.FC<Props> = ({ open, initialName = '', onClose, onCreated }) => {
  const { lang } = useDirection();
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState('');
  const [maxGrade, setMaxGrade] = useState(7);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setName(initialName);
      setDescription('');
      setMaxGrade(7);
      setError('');
    }
  }, [open, initialName]);

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError(tr('param.nameRequired', lang));
      return;
    }
    setBusy(true);
    setError('');
    try {
      const created = await apiService.createParameter({
        name: trimmed,
        ...(description.trim() ? { description: description.trim() } : {}),
        maxGrade,
      });
      onCreated(created);
    } catch (e: any) {
      setError(apiErrorMessage(e, tr('param.saveError', lang)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{tr('param.addTitle', lang)}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          label={tr('param.name', lang)}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !busy) {
              e.preventDefault();
              submit();
            }
          }}
          autoFocus
          required
          size="small"
        />
        <TextField
          label={tr('param.description', lang)}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          minRows={2}
          size="small"
        />
        <TextField
          select
          label={tr('param.scale', lang)}
          value={maxGrade}
          onChange={(e) => setMaxGrade(Number(e.target.value))}
          size="small"
        >
          {SCALES.map((n) => (
            <MenuItem key={n} value={n}>
              <span dir="ltr">{`1–${n}`}</span>
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{tr('param.cancel', lang)}</Button>
        <Button variant="contained" onClick={submit} disabled={busy || !name.trim()}>
          {tr('param.create', lang)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewParameterDialog;
